import React from 'react'

import styled from 'styled-components'
import { SvgIcon, SvgIconType } from 'components/styled/icon/svg'
import { ButtonVariant } from 'components/styled/button/base-button'
import { SubtitleStyle } from '../typography/styles'
import { Icon } from '../icon'
import { LoadingIndicator } from '../loading-indicator'
import { Ellipsis } from '../typography'

export interface ButtonContentProps {
  variant?: ButtonVariant
  icon?: string
  iconRight?: string
  useOutlinedIcon?: boolean
  useOutlinedRightIcon?: boolean
  svgIcon?: SvgIconType
  text?: string
  loading?: boolean
  size?: 'small' | 'default'
  flippedIcon?: boolean
}

export const ButtonText = styled.span<{
  $hasIcon: boolean
  $text: string
  size: 'small' | 'default'
  $loading?: boolean
}>`
  ${SubtitleStyle};
  color: inherit;
  white-space: nowrap;
  font-size: ${({ size, theme }) => size === 'small' && theme.space.s14};
  ${Ellipsis};
  line-height: 2.4rem;
  padding: ${({ theme, $hasIcon, $loading }) =>
    $hasIcon || $loading ? `0 ${theme.space.s12}` : 0};

  ${({ theme }) => theme.mediaQuery.phone} {
    white-space: ${({ $text }) => ($text.length > 15 ? 'normal' : 'nowrap')};
  }
`

const getLoaderColor = (buttonVariant: ButtonVariant) => {
  switch (buttonVariant) {
    case 'secondary':
    case 'secondaryGhost':
    case 'tertiary':
    case 'basicSecondary':
    case 'tertiaryGhost':
      return 'grey30'
    case 'primary':
      return 'white'
    case 'primaryGhost':
    case 'basic':
      return 'primary30'
    default:
      return 'primary30'
  }
}

// This is the button content component for all buttons, and links that share same styles.
// This component shouldn't be used for anything else.
// Use Button, LinkButton, or ExternalLinkButton components instead.

export const ButtonContent = ({
  variant = 'primary',
  icon,
  iconRight,
  useOutlinedIcon = false,
  useOutlinedRightIcon = false,
  svgIcon,
  text,
  loading,
  size = 'default',
  flippedIcon = false,
}: ButtonContentProps) => {
  const loaderColor = getLoaderColor(variant)

  return (
    <>
      {icon && !loading && (
        <Icon
          aria-hidden
          type={icon}
          useOutlined={useOutlinedIcon}
          size={size === 'default' ? 's24' : 's16'}
          flipped={flippedIcon}
        />
      )}
      {svgIcon && !loading && (
        <SvgIcon
          aria-hidden
          type={svgIcon}
          size={size === 'default' ? 's24' : 's16'}
        />
      )}
      {loading && (
        <LoadingIndicator
          color={loaderColor}
          size={size === 'small' ? 's16' : 's24'}
          centerize={false}
        />
      )}
      {text && (
        <ButtonText
          $text={text}
          $loading={loading}
          size={size}
          $hasIcon={!!icon || !!svgIcon || !!iconRight}
        >
          {text}
        </ButtonText>
      )}
      {iconRight && !loading && (
        <Icon
          aria-hidden
          type={iconRight}
          useOutlined={useOutlinedRightIcon}
          size={size === 'default' ? 's24' : 's16'}
        />
      )}
    </>
  )
}
